import React, { useEffect, useState } from 'react';
import Alert from 'components/ui/Alert';
import Label from 'components/v2/forms/Label';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import CargomaticDatePicker from 'components/v2/DatePicker';
import CargomaticSelect from 'components/v2/select';

function Asterisk() {
  return <span className="text-xs font-medium text-gray-500">*</span>;
}

const ContractForm = ({
  data = {},
  activeTermsAndConditions = [],
  saving,
  onValidSuccess,
  onValidFail,
}) => {
  const [errors, setErrors] = useState({});
  const [formFields, setFormFields] = useState({
    start_date: null,
    end_date: null,
    contractFile: null,
    ...data,
    terms_and_conditions:
      data?.terms_and_conditions?.[0]?._id ||
      activeTermsAndConditions?.[0]?._id,
  });
  const { t } = useTranslation();
  const TODAY = moment().startOf('day');
  const isActiveContract =
    data?.active && data?._id && data?.status === 'active';

  const validateDates = (startDate, endDate) => {
    const dateErrors = {};
    let startDateMoment = moment.isMoment(startDate)
      ? startDate
      : moment(startDate);
    let endDateMoment = moment.isMoment(endDate) ? endDate : moment(endDate);

    if (
      moment(endDateMoment.format('YYYY-MM-DD')).isBefore(
        moment(startDateMoment.format('YYYY-MM-DD')),
      )
    ) {
      dateErrors.endDate = t('END_DATE_BEFORE_START_DATE');
    }

    if (!isActiveContract && startDateMoment.isBefore(TODAY)) {
      dateErrors.startDate = t('START_DATE_IN_PAST');
    }

    return dateErrors;
  };

  const validate = () => {
    let formErrors = {};

    if (!formFields.start_date) {
      formErrors.startDate = t('PROVIDE_START_DATE');
    }

    if (!formFields.end_date) {
      formErrors.endDate = t('PROVIDE_END_DATE');
    }

    if (formFields.start_date && formFields.end_date) {
      const dateErrors = validateDates(
        formFields.start_date,
        formFields.end_date,
      );
      if (Object.keys(dateErrors).length) {
        return dateErrors;
      }
    }

    if (!formFields.terms_and_conditions) {
      formErrors.terms_and_conditions = t('TERMS_AND_CONDITIONS_MANDATORY');
    }

    return formErrors;
  };

  const renderErrors = () => {
    if (Object.keys(errors).length === 0) {
      return null;
    }

    return (
      <Alert type="error" title={t('FIX_FOLLOWING_ISSUES')} className="mb-4">
        <ul data-testid="quickpay-error">
          {Object.values(errors).map((error, i) => (
            <li key={i}>{error}</li>
          ))}
        </ul>
      </Alert>
    );
  };

  const handleChange = (name, value) => {
    setFormFields({
      ...formFields,
      [name]: value,
    });
  };

  const handleDateChangeRaw = (field, value) => {
    if (!value.includes('_')) {
      handleChange(field, getDate(value));
    } else if (formFields[field]) {
      handleChange(field, '');
    }
  };

  const getData = () => {
    const { start_date, end_date, terms_and_conditions } = formFields;

    return {
      start_date,
      end_date,
      terms_and_conditions,
    };
  };

  const saveForm = async () => {
    const formErrors = validate();

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      onValidFail();
      return;
    }

    onValidSuccess(getData());
    setErrors({});
  };

  useEffect(() => {
    if (saving) {
      saveForm();
    }
  }, [saving]);

  const getDate = (value) => {
    if (!value) {
      return '';
    }

    return moment(value);
  };

  return (
    <>
      {renderErrors()}
      <div
        data-testid="contract-form"
        className="flex flex-col sm:flex-row justify-start sm:items-center sm:space-x-8 space-y-4 sm:space-y-0 "
      >
        <div>
          <Label htmlFor="variableStartDate">
            {t('START_DATE')} <Asterisk />
          </Label>
          <CargomaticDatePicker
            disabled={isActiveContract}
            className="mt-1"
            selected={formFields?.start_date}
            onChange={(val) => handleChange('start_date', val)}
            onChangeRaw={({ target: { value } }) =>
              handleDateChangeRaw('start_date', value)
            }
            minDate={getDate(new Date())}
          />
        </div>
        <div>
          <Label htmlFor="variableEndDate">
            {t('END_DATE')} <Asterisk />
          </Label>
          <CargomaticDatePicker
            className="mt-1"
            selected={formFields?.end_date}
            onChange={(val) => handleChange('end_date', val)}
            onChangeRaw={({ target: { value } }) =>
              handleDateChangeRaw('end_date', value)
            }
            minDate={
              formFields?.start_date
                ? getDate(formFields.start_date)
                : getDate(new Date())
            }
          />
        </div>
        <div>
          <CargomaticSelect
            showCaption
            label={t('TERMS_AND_CONDITIONS_ASTERISK')}
            selected={formFields.terms_and_conditions}
            items={activeTermsAndConditions}
            onSelect={(item) => {
              handleChange('terms_and_conditions', item._id);
            }}
            applyDefaultMargins={false}
            valueKey="_id"
            textKey="displayText"
            disabled={!activeTermsAndConditions?.length}
          />
        </div>
      </div>
    </>
  );
};

export default ContractForm;
